import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, Plus, Settings, ScrollText, Check, FileCode2, TriangleAlert, FileStack, MonitorPlay, DatabaseBackup, LogOut, ShieldCheck, Share2, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";
import { ProposalPdfButton } from "@/components/topbar/ProposalPdfButton";
import { resolveRoute } from "@/constants/deployTargets";
import { SiteHealthCard } from "@/components/topbar/SiteHealthCard";
import { DeployHistory } from "@/components/topbar/DeployHistory";
import { AddSiteDialog } from "@/components/topbar/AddSiteDialog";
import { SiteSettingsSheet } from "@/components/topbar/SiteSettingsSheet";
import { DeployActions } from "@/components/topbar/DeployActions";

const EnvChip = ({ environment }) => (
  <span
    data-testid="topbar-env-chip"
    className={`inline-flex items-center gap-1 rounded-[6px] px-1.5 py-0.5 font-nunito text-[8.5px] font-extrabold uppercase tracking-[0.14em] border ${
      environment === "live"
        ? "bg-[#e9f1fd] text-[#0050c0] border-[#c8dcf7]"
        : "bg-[#fef2e5] text-[#c25a00] border-[#f8ddc0]"
    }`}
  >
    <span className={`h-1.5 w-1.5 rounded-full ${environment === "live" ? "bg-[#0050c0]" : "bg-[#f07000]"}`} />
    {environment === "live" ? "Live" : "Staging"}
  </span>
);

/**
 * Thin always-visible app chrome (light gray): site switcher with live health
 * scorecard + red alerting, active-page tracking, per-page deploy actions.
 * Two-row layout on mobile so nothing truncates.
 */
export const TopBar = () => {
  const { sites, activeSite, setActiveSiteId, healthMap, previewToolsOpen, setPreviewToolsOpen, settingsOpen, setSettingsOpen } = useSite();
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [sharing, setSharing] = useState(false);

  // Client share link: fetch (create-if-missing) the workspace's secret
  // preview URL and put it on the clipboard - the client sees ONLY this
  // workspace under a buttonless WMM watermark bar.
  const shareSite = async () => {
    if (!activeSite || activeSite.is_seed || sharing) return;
    setSharing(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/sites/${activeSite.id}/share-link`
      );
      let copied = false;
      try {
        await navigator.clipboard.writeText(data.url);
        copied = true;
      } catch (err) { /* clipboard unavailable - still offer Open */ }
      toast.success(
        copied
          ? `Client preview link copied — ${activeSite.name} only.`
          : `Client preview link ready: ${data.url}`,
        { action: { label: "Open", onClick: () => window.open(data.url, "_blank", "noopener") }, duration: 6000 }
      );
    } catch (e) {
      toast.error("Couldn't create the share link — try again.");
    } finally {
      setSharing(false);
    }
  };

  // Switching workspaces reloads the whole interface into that site's
  // Emergent version: seed -> React mirror, everything else -> mockup viewer.
  const switchSite = (s) => {
    setActiveSiteId(s.id);
    setSwitcherOpen(false);
    navigate(s.is_seed ? "/" : "/preview");
  };

  const route = resolveRoute(location.pathname);
  const showDeploy = route.deployable && activeSite?.deploy_capable;

  const health = activeSite ? healthMap[activeSite.id] : null;
  const healthAlert =
    health && !health.error && (health.connection?.ok === false || health.last_deploy?.status === "failed");

  return (
    <>
      <div
        data-testid="topbar"
        className="fixed top-0 left-0 right-0 z-[100] bg-[#f2f2f3] border-b border-black/10 shadow-[0_2px_14px_rgba(2,2,2,0.08)]"
      >
        <div className="px-3 sm:px-4 flex flex-wrap items-center gap-x-2 sm:gap-x-3">
          {/* Row 1 (mobile, full width) / left cluster (desktop): brand + site switcher */}
          {/* Row 1 (mobile) / far left (desktop): brand + tool label */}
          <div className="order-1 flex h-9 sm:h-11 min-w-0 items-center gap-2 select-none">
            <img src="/branding/wmm-logo-full.png" alt="Web Marketing Machine" className="h-5 w-5 sm:h-6 sm:w-6 shrink-0" />
            <div
              className="flex min-w-0 flex-col leading-[1.2] sm:flex-row sm:items-center sm:gap-2"
              style={{ fontFamily: '"IBM Plex Sans", ui-sans-serif, sans-serif' }}
            >
              <span className="truncate text-[10px] sm:text-[11px] font-semibold text-[#0050c0]">Web Marketing Machine</span>
              <span className="hidden sm:block h-3 w-px bg-black/15" />
              <span data-testid="topbar-app-label" className="truncate text-[8.5px] sm:text-[10px] font-semibold uppercase tracking-[0.06em] text-[#8a8a8a]">
                WordPress Website Builder
              </span>
            </div>
          </div>

          {/* Row 2 (mobile) / second cluster (desktop): site switcher + PDF */}
          <div className="order-3 sm:order-2 basis-full sm:basis-auto flex h-9 sm:h-11 min-w-0 items-center gap-2 border-t border-black/10 sm:border-t-0">
            <Popover open={switcherOpen} onOpenChange={setSwitcherOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="topbar-site-switcher-trigger"
                  className={`flex min-w-0 items-center gap-2 rounded-[8px] px-2 py-1.5 transition-colors duration-150 ${
                    healthAlert ? "bg-[#fef2e5] hover:bg-[#fde7d2]" : "hover:bg-black/[0.05]"
                  }`}
                >
                  {healthAlert && <TriangleAlert size={13} className="text-[#c25a00] shrink-0" />}
                  <span data-testid="topbar-active-site-name" className="truncate max-w-[55vw] sm:max-w-[180px] font-nunito text-[11px] font-extrabold text-[#383838]">
                    {activeSite ? activeSite.name : "Loading…"}
                  </span>
                  {activeSite && <EnvChip environment={activeSite.environment} />}
                  {healthAlert && (
                    <span data-testid="topbar-health-alert" className="inline-flex items-center gap-1 rounded-[6px] border border-[#f8ddc0] bg-[#fef2e5] px-1.5 py-0.5 font-nunito text-[8.5px] font-extrabold uppercase tracking-[0.12em] text-[#c25a00]">
                      Alert
                    </span>
                  )}
                  <ChevronDown size={12} className={`text-[#8a8a8a] transition-transform duration-150 ${switcherOpen ? "rotate-180" : ""}`} />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                sideOffset={6}
                data-testid="topbar-site-switcher-panel"
                className="w-[340px] rounded-[14px] bg-white border-black/10 text-[#383838] p-3 shadow-[0_20px_60px_rgba(0,0,0,0.18)]"
              >
                <p className="font-nunito text-[9px] font-extrabold uppercase tracking-[0.18em] text-[#9a9a9a]">Site Workspaces</p>
                <div className="mt-2 space-y-1">
                  {sites.map((s) => {
                    const sh = healthMap[s.id];
                    const sAlert = sh && !sh.error && (sh.connection?.ok === false || sh.last_deploy?.status === "failed");
                    return (
                      <button
                        key={s.id}
                        type="button"
                        data-testid={`topbar-site-option-${s.id}`}
                        onClick={() => switchSite(s)}
                        className={`w-full flex items-center gap-2 rounded-[10px] px-2.5 py-2 text-left transition-colors duration-150 border ${
                          s.id === activeSite?.id ? "bg-black/[0.05] border-black/10" : "hover:bg-black/[0.04] border-transparent"
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-nunito text-[12px] font-extrabold text-[#383838] truncate">{s.name}</span>
                            <EnvChip environment={s.environment} />
                            {sAlert && <TriangleAlert size={12} className="text-[#c25a00] shrink-0" />}
                          </div>
                          <span className="block text-[10px] text-[#8a8a8a] truncate">{s.production_domain}</span>
                        </div>
                        {s.id === activeSite?.id && <Check size={14} className="text-[#0050c0] shrink-0" />}
                      </button>
                    );
                  })}
                </div>

                {/* Live health scorecard for the active site */}
                {activeSite && switcherOpen && (
                  <>
                    <Separator className="my-3 bg-black/10" />
                    <p className="font-nunito text-[9px] font-extrabold uppercase tracking-[0.18em] text-[#9a9a9a]">
                      {activeSite.name} · Health
                    </p>
                    <SiteHealthCard siteId={activeSite.id} />
                    <Separator className="my-3 bg-black/10" />
                    <DeployHistory siteId={activeSite.id} />
                  </>
                )}

                <Separator className="my-3 bg-black/10" />
                <button
                  type="button"
                  data-testid="topbar-add-site-button"
                  onClick={() => {
                    setSwitcherOpen(false);
                    setAddOpen(true);
                  }}
                  className="w-full flex items-center justify-center gap-1.5 rounded-[10px] border border-dashed border-black/25 px-3 py-2 font-nunito text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#6e6e6e] hover:text-[#383838] hover:border-black/50 transition-colors duration-150"
                >
                  <Plus size={13} />
                  Add Site
                </button>
              </PopoverContent>
            </Popover>
            <div className="ml-auto shrink-0 sm:ml-1">
              <ProposalPdfButton />
            </div>
          </div>

          {/* Row 1 right (mobile) / far right (desktop): tool icons */}
          <div className="order-2 sm:order-4 ml-auto flex h-9 sm:h-11 shrink-0 items-center gap-2 sm:gap-2.5">
            {activeSite && !activeSite.is_seed && (
              <button
                type="button"
                data-testid="topbar-share-button"
                aria-label="Copy client preview link"
                title="Copy client preview link"
                onClick={shareSite}
                disabled={sharing}
                className="text-[#8a8a8a] hover:text-[#0050c0] transition-colors duration-150 disabled:opacity-50"
              >
                {sharing ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
              </button>
            )}
            <Link
              to={activeSite?.is_seed ? "/" : "/preview"}
              data-testid="topbar-viewer-link"
              aria-label="View site mockup"
              className="text-[#8a8a8a] hover:text-[#383838] transition-colors duration-150"
            >
              <MonitorPlay size={16} />
            </Link>
            <Link
              to="/workspace"
              data-testid="topbar-workspace-link"
              aria-label="Workspace pages"
              className="text-[#8a8a8a] hover:text-[#383838] transition-colors duration-150"
            >
              <FileStack size={16} />
            </Link>
            <Link
              to="/deploy"
              data-testid="topbar-deploy-log-link"
              aria-label="Deploy Engine log"
              className="text-[#8a8a8a] hover:text-[#383838] transition-colors duration-150"
            >
              <ScrollText size={16} />
            </Link>
            <Link
              to="/sync"
              data-testid="topbar-sync-link"
              aria-label="Data sync"
              className="text-[#8a8a8a] hover:text-[#383838] transition-colors duration-150"
            >
              <DatabaseBackup size={16} />
            </Link>
            <Link
              to="/sso-check"
              data-testid="topbar-sso-check-link"
              aria-label="SSO handoff smoke test"
              className="text-[#8a8a8a] hover:text-[#383838] transition-colors duration-150"
            >
              <ShieldCheck size={16} />
            </Link>
            <button
              type="button"
              data-testid="topbar-settings-button"
              aria-label="Workspace settings"
              onClick={() => setSettingsOpen(true)}
              className="text-[#8a8a8a] hover:text-[#383838] transition-colors duration-150"
            >
              <Settings size={16} />
            </button>
            <Separator orientation="vertical" className="h-4 bg-black/10" />
            <button
              type="button"
              data-testid="topbar-logout-button"
              aria-label="Sign out"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              className="text-[#8a8a8a] hover:text-[#d96400] transition-colors duration-150"
            >
              <LogOut size={16} />
            </button>
          </div>

          {/* Row 3 (mobile, full width) / middle (desktop): page tracker + deploy actions */}
          <div className="order-4 sm:order-3 basis-full sm:basis-auto sm:flex-1 flex h-9 sm:h-11 items-center gap-2 border-t border-black/10 sm:border-t-0 min-w-0">
            <Separator orientation="vertical" className="hidden sm:block h-5 bg-black/10" />
            <div data-testid="topbar-active-page" className="flex items-center gap-1.5 min-w-0">
              <FileCode2 size={12} className="text-[#9a9a9a] shrink-0" />
              <span className="font-nunito text-[9px] font-extrabold uppercase tracking-[0.14em] text-[#9a9a9a]">Page</span>
              <span className="font-nunito text-[11px] font-extrabold text-[#383838] truncate max-w-[150px] md:max-w-[220px]">
                {location.pathname === "/preview" && activeSite?.preview_mode === "live" ? "Live Site" : route.label}
              </span>
            </div>
            {location.pathname === "/preview" && activeSite && !activeSite.is_seed && (
              <button
                type="button"
                data-testid="topbar-page-details-toggle"
                onClick={() => setPreviewToolsOpen(!previewToolsOpen)}
                className={`inline-flex shrink-0 items-center gap-1 rounded-[6px] border px-1.5 py-0.5 font-nunito text-[8.5px] font-extrabold uppercase tracking-[0.1em] transition-colors duration-150 ${
                  previewToolsOpen
                    ? "border-[#c8dcf7] bg-[#e9f1fd] text-[#0050c0]"
                    : "border-black/15 text-[#6e6e6e] hover:border-[#0050c0]/40 hover:text-[#0050c0]"
                }`}
              >
                <Plus size={10} className={`transition-transform duration-150 ${previewToolsOpen ? "rotate-45" : ""}`} />
                Page details
              </button>
            )}
            <div className="ml-auto flex items-center">
              {showDeploy ? (
                <DeployActions key={`${activeSite?.id}-${route.target}`} target={route.target} label={route.label} />
              ) : activeSite && !activeSite.deploy_capable ? (
                <Link
                  to="/workspace"
                  data-testid="topbar-manage-pages-link"
                  className="font-nunito text-[9px] font-extrabold uppercase tracking-[0.14em] text-[#0050c0] hover:text-[#0343a0] hover:underline"
                >
                  Manage pages →
                </Link>
              ) : (
                <span data-testid="topbar-deploy-unavailable" className="font-nunito text-[9px] font-extrabold uppercase tracking-[0.14em] text-[#9a9a9a]">
                  No deploy target
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <AddSiteDialog open={addOpen} onOpenChange={setAddOpen} />
      <SiteSettingsSheet open={settingsOpen} onOpenChange={setSettingsOpen} />
    </>
  );
};
